import { Ship, Anchor, Compass, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { mareWidget } from '@/lib/widgets';
import type { DiaMareInterpretado } from '@/types/mare';

interface MareCtaProps {
  diaPrincipal: DiaMareInterpretado;
  t: (key: string) => string;
  lang: string;
}

function getWhatsAppUrl(phone: string, message: string): string {
  const cleanPhone = phone.replace(/\D/g, '');
  const encoded = encodeURIComponent(message);
  return `https://wa.me/55${cleanPhone}?text=${encoded}`;
}

function getCtaConfig(diaPrincipal: DiaMareInterpretado, t: (key: string) => string) {
  if (diaPrincipal.interpretacao === 'maré-alta') {
    return {
      tipo: 'whatsapp' as const,
      texto: t('widget.mare.ctaAlta'),
      url: getWhatsAppUrl(
        '41992900073',
        'Oi Carol! Vi no vemprailhadomel que hoje tem maré alta e queria saber mais sobre o Passeio 360° (via widget-mare)'
      ),
      icone: <Ship className="w-4 h-4" />,
      estilo: 'bg-gradient-to-r from-coral to-[#d4715a] text-white shadow-lg shadow-coral/20 hover:-translate-y-0.5',
    };
  }

  if (diaPrincipal.interpretacao === 'maré-média') {
    return {
      tipo: 'experiencias' as const,
      texto: t('widget.mare.ctaMedia'),
      url: '/experiencias?utm_source=vemprailhadomel&utm_medium=widget&utm_campaign=mare-media',
      icone: <Compass className="w-4 h-4" />,
      estilo: 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100',
    };
  }

  return {
    tipo: 'wikiloc' as const,
    texto: t('widget.mare.ctaBaixa'),
    url: 'https://wikiloc.com/trails/hiking/brazil/parana/ilha-do-mel?utm_source=vemprailhadomel&utm_medium=widget&utm_campaign=mare-baixa',
    icone: <Anchor className="w-4 h-4" />,
    estilo: 'bg-teal-50 border border-teal-200 text-teal-deep hover:bg-teal-100',
  };
}

export function MareCta({ diaPrincipal, t, lang }: MareCtaProps) {
  const cta = getCtaConfig(diaPrincipal, t);

  return (
    <a
      href={cta.url}
      target={cta.tipo === 'experiencias' ? undefined : '_blank'}
      rel={cta.tipo === 'experiencias' ? undefined : 'noopener noreferrer'}
      onClick={() =>
        mareWidget.trackClick({
          cta_tipo: cta.tipo,
          mare_nivel: diaPrincipal.interpretacao,
          coeficiente: diaPrincipal.coeficiente,
          lang,
        })
      }
      className={cn(
        'block w-full rounded-xl px-5 py-4 text-center font-body font-bold transition-all',
        cta.estilo
      )}
    >
      <span className="flex items-center justify-center gap-2">
        {cta.icone}
        {cta.texto}
        <ArrowUpRight className="w-4 h-4" />
      </span>
    </a>
  );
}
